/* eslint-disable jsx-control-statements/jsx-jcs-no-undef */
import load from 'load-script';
import { isDev, isBrowser, pipelineEnv } from './env';
import { runAsyncSafe } from './utils';

// @ts-ignore
let uniframePromise: Promise<Window['BybitUniFrame']>;

// @ts-ignore
export const getUniFrame = async (): Promise<Window['BybitUniFrame']> => {
  if (!isBrowser) {
    return Promise.reject(new Error('Unsupported in SSR'));
  }
  const parentGetUniFrame = (window as any).$wujie?.props?.getUniFrame;
  if (typeof parentGetUniFrame === 'function') {
    return parentGetUniFrame();
  }
  // @ts-ignore
  if (window.BybitUniFrame) {
    // @ts-ignore
    if (window.BybitUniFrame.isReady) {
      // @ts-ignore
      return window.BybitUniFrame;
    }
    let checkThread: any;
    // @ts-ignore
    return Promise.race<Window['BybitUniFrame']>([
      new Promise((resolve) => {
        checkThread = setInterval(() => {
          // @ts-ignore
          if (window.BybitUniFrame?.isReady) {
            // @ts-ignore
            resolve(window.BybitUniFrame);
            clearInterval(checkThread);
          }
        }, 1e3);
      }),
      new Promise((_, reject) => {
        setTimeout(() => reject(new Error('uniframe load timeout')), 10e3);
      }),
    ]);
  }
  if (uniframePromise) {
    return uniframePromise;
  }

  uniframePromise = new Promise((resolve, reject) => {
    const uniframeUrl = `${isDev ? `https://www.bybit.com` : ''}/uniframe/uniframe.js?v=${Date.now()}`;
    const attr = 'data-uniframe';
    const opts = {
      attrs: {
        [attr]: '1',
      },
    };
    // 如果已存在就不加载了
    if (document.querySelector(`script[${attr}="1"]`)) {
      return;
    }
    load(uniframeUrl, opts, (err) => {
      if (!err) {
        // 这个就去走上面的 window 对象的过程了
        return getUniFrame().then(resolve).catch(reject);
      }
      document.querySelector(`script[${attr}="1"]`)?.remove();
      reject(err);
    });
  });
  return uniframePromise;
};

export const getVerifiedRiskToken = async (opts: {
  username?: string;
  country_code?: string;
  token: string;
  onSave?: (token: string) => void;
  onCancel?: () => void;
  onClose?: () => void;
}) => {
  const uniframe = await getUniFrame();
  const { security } = uniframe;
  const { properties, events } = security;
  const { VISIBLE, USER, RISK_TOKEN } = properties;
  const { SAVE_HANDLER, CANCEL_HANDLER, CLOSE_MODAL_HANDLER } = events;

  security.setProperty(USER, {
    username: opts.username ?? '',
    country_code: opts.country_code ?? '',
  });

  security.setProperty(RISK_TOKEN, opts.token);
  security.setProperty(VISIBLE, true);
  try {
    if (document.activeElement) (document.activeElement as any).blur();
  } catch (err) {
    //
  }

  const close = () => {
    security.setProperty(USER, {
      username: '',
      country_code: '',
    });
    security.setProperty(RISK_TOKEN, null);
    security.setProperty(VISIBLE, false);
  };

  return new Promise<string>((resolve, reject) => {
    const onSave = (res) => {
      // 只有通过之后才会有事件
      const token = (res.result?.risk_token || '') as string;
      opts.onSave?.(token);
      close();
      removeEvents();
      resolve(token);
    };
    const onCancel = () => {
      opts.onCancel?.();
      close();
      removeEvents();
      reject(new Error('user canceled security verification.'));
    };
    const onClose = () => {
      opts.onClose?.();
      close();
      removeEvents();
      reject(new Error('user canceled security verification.'));
    };

    const removeEvents = () => {
      security.unsubscribeEvent(SAVE_HANDLER, onSave);
      security.unsubscribeEvent(CANCEL_HANDLER, onCancel);
      security.unsubscribeEvent(CLOSE_MODAL_HANDLER, onClose);
    };

    security.subscribeEvent(SAVE_HANDLER, onSave);
    security.subscribeEvent(CANCEL_HANDLER, onCancel);
    security.subscribeEvent(CLOSE_MODAL_HANDLER, onClose);
  });
};

// 显示登陆弹窗
export const showLoginModal = async (onLogin?: () => void) => {
  const uniframe = await getUniFrame();
  const { loginModal } = uniframe;
  const { VISIBLE } = loginModal.properties;
  const { LOGIN_SUCCESS, HIDE_MODAL } = loginModal.events;
  loginModal
    .setProperty(VISIBLE, true)
    .subscribeEvent(LOGIN_SUCCESS, () => {
      loginModal.setProperty(VISIBLE, false);
      onLogin?.();
    })
    .subscribeEvent(HIDE_MODAL, () => {
      loginModal.setProperty(VISIBLE, false);
    });
};
// 显示注册弹窗
export const showRegisterModal = async (onRegister?: () => void) => {
  const { registerModal } = await getUniFrame();
  const { VISIBLE } = registerModal.properties;
  const { REGISTER_SUCCESS, HIDE_MODAL } = registerModal.events;

  registerModal
    .setProperty(VISIBLE, true)
    .subscribeEvent(REGISTER_SUCCESS, async () => {
      registerModal.setProperty(VISIBLE, false);
      onRegister?.();
    })
    .subscribeEvent(HIDE_MODAL, () => {
      registerModal.setProperty(VISIBLE, false);
    });
};

// 显示分享弹窗
export const showShareModal = async (data: {
  posterStyle: string[];
  link: string;
  content: string;
  channels: string[];
  templateData: unknown;
  verticalImage?: string;
  horizontalImage?: string;
  code?: string;
}) => {
  const { share } = await getUniFrame();
  const { VISIBLE, CONFIG } = share.properties;
  share.setProperty(CONFIG, {
    theme: 'dark',
    posterStyle: ['vertical'],
    link: data.link,
    code: data.code,
    content: data.content,
    channels: data.channels,
    poster: [
      {
        template: 'default',
        templateData: data.templateData,
      },
    ],
  });
  share.setProperty(VISIBLE, true);
};

export const showBybitWithDraw = async (
  params: {
    coin: string;
    user: any;
    chainCode?: string;
    tips?: string;
  },
  {
    zIndex = 3000,
    onWithdrawSuccess,
    onOpen = () => {},
  }: {
    zIndex?: number;
    onWithdrawSuccess?: () => void;
    onOpen?: () => void;
  } = {}
) => {
  const { withdraw } = await getUniFrame();
  const { properties, events, enums } = withdraw;
  const {
    VISIBILE,
    COIN,
    POSITION_TYPE,
    THEME,
    USER,
    DEFAULT_CHAIN,
    CUSTOM_TIPS,
    ZINDEX,
  } = properties;
  const { HIDE_MODAL, WITHDRAW_SUCCESS, OPEN } = events;

  withdraw.setProperty(POSITION_TYPE, 'web3');
  withdraw.setProperty(THEME, enums.THEME.LIGHT);
  withdraw.setProperty(USER, params.user);
  withdraw.setProperty(COIN, params.coin);
  withdraw.setProperty(DEFAULT_CHAIN, params.chainCode || '');
  withdraw.setProperty(CUSTOM_TIPS, params.tips || '');
  withdraw.setProperty(VISIBILE, true);
  withdraw.setProperty(ZINDEX, zIndex);

  const hide = () => {
    withdraw.setProperty(VISIBILE, false);
    dispose();
  };
  const withDrawSuccess = () => {
    onWithdrawSuccess?.();
    hide();
  };

  const dispose = () => {
    withdraw.unsubscribeEvent(HIDE_MODAL, hide);
    withdraw.unsubscribeEvent(WITHDRAW_SUCCESS, withDrawSuccess);
    withdraw.unsubscribeEvent(OPEN, onOpen);
  };

  withdraw.subscribeEvent(HIDE_MODAL, hide);
  withdraw.subscribeEvent(WITHDRAW_SUCCESS, withDrawSuccess);

  withdraw.subscribeEvent(OPEN, onOpen);

  return hide;
};

// 详见文档：https://uponly.larksuite.com/wiki/wikusHPWtCoUcg9LrV0o5WcX47g
export type CaptchaSuccess = {
  //传给后端的 流水号、令牌号
  serialNo: string;
  // 令牌有效时间
  expire_time: number;
  // 极验4.0的兜底验证，一键通过
  byPass?: boolean;
  // 无需校验直接通过
  pass?: boolean;
};
export type CaptchaFail = {
  /**
   * 80001: order接口错误（2s超时 / 网络错误 / 返回内容错误 / 接口ret_code不为0 / 可用范围为空 / 从可用范围中找不到对应实例）
    80002: 命中黑名单，校验不通过
    80003: 用户完成行为验证，但是验证结果在后端校验失败
    80004: 所有行为验证都不可用
   */
  code: number;
  // 错误信息，uniframe 给的是msg
  msg?: string;
  // 这里给统一成 message，和接口字段一样
  message: string;
};

/**
 * 文档：https://uponly.larksuite.com/wiki/wikusHPWtCoUcg9LrV0o5WcX47g
 * 打开uniframe 喜鹊验证码组件，
 * @param param0 验证码组件参数
 */
export const openCaptchaV2 = async ({
  scene,
  immediate = true,
  txid = '',
  loginName = '',
  countryCode = '',
  showErrorToast = true,
  onSuccess,
  onFail,
  onShow,
}: {
  // 场景码：找 jason.liang 申请
  scene: string;
  immediate?: boolean;
  // 非必填，设备指纹
  txid?: string;
  // 用户登陆名，非必填
  loginName?: string;
  // 国家码，非必须
  countryCode?: string;
  // 是否toast
  showErrorToast?: boolean;
  // 用户验证成功的 回调函数，可以直接用promise接住
  onSuccess?: (info: CaptchaSuccess) => void;
  // 失败时 的回调，指用户操作失败，但是一般会让用户尝试，所以这个事件也没什么意义，所以只需要关注onSucess 即可
  onFail?: (fail: CaptchaFail) => void;
  // name 是使用的验证码组件名字，一般这个回调没什么用
  onShow?: (name: string) => void;
}) => {
  const [err, [captchaV2, capInstance]] = await runAsyncSafe(async () => {
    // @ts-ignore
    const { captchaV2 } = await getUniFrame();
    return [captchaV2, await captchaV2.init()];
  });

  if (!capInstance) {
    console.error(err);
    // 返回这个格式是为了保持和 fail reject的结果 一致
    throw {
      // 加载错误（一般不会发生)
      code: 80005,
      message: 'load uniframe captcha error',
    };
  }

  const { PARAMS, EVENT_NAME } = captchaV2.properties;
  const { ERROR, SUCCESS, SHOW } = captchaV2.events;

  return new Promise<CaptchaSuccess>((resolve, reject) => {
    const _onSuccess = (info: CaptchaSuccess) => {
      onSuccess?.(info);
      unSubScrible();
      resolve(info);
    };
    const _onFail = (fail: CaptchaFail) => {
      fail.message = fail.msg;
      onFail?.(fail);
    };
    const _onShow = (name: any) => {
      onShow?.(name);
    };

    const unSubScrible = () => {
      capInstance.unsubscribeEvent(SUCCESS, _onSuccess);
      capInstance.unsubscribeEvent(ERROR, _onFail);
      capInstance.unsubscribeEvent(SHOW, _onShow);
    };

    const subscrible = () => {
      capInstance.subscribeEvent(SUCCESS, _onSuccess);
      capInstance.subscribeEvent(ERROR, _onFail);
      capInstance.subscribeEvent(SHOW, _onShow);
    };
    capInstance.setProperty(PARAMS, {
      scene,
      immediate,
      txid,
      login_name: loginName,
      country_code: countryCode,
      showErrorToast,
    });
    subscrible();
    if (!immediate) {
      capInstance.setProperty(EVENT_NAME, 'triggerClick');
    }
  });
};

/** 客服机器人 */
export const initAdaBot = async () => {
  const { isReady, adaBot } = await getUniFrame();
  console.log('adaBot isReady:', isReady);
  if (isReady) {
    adaBot.init();
    return;
  } else {
    setTimeout(() => initAdaBot, 300);
  }
};
